import React, { useEffect, useState, useContext } from "react";
import { FaSort, FaSortDown, FaSortUp } from "react-icons/fa";
import { saveAs } from "file-saver";
import Table from "react-bootstrap/Table";
import Modal from "react-bootstrap/Modal";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { AiFillStar } from "react-icons/ai";
import { MdContentCopy } from "react-icons/md";
import Papa from "papaparse";
import Loading from "../loader/Loading";
import { Ucontext } from "../../context/Ucontext";
import { BASE_URL, REACT_APP_URL } from "../../config/index";
import { useNavigate } from "react-router-dom";
import {
  adminLogin,
  downloadDocuSignDoc,
  downloadTinkReport,
  getTenantKycDocs,
  updateUserInfo,
  getDocumentAnalysis,
  runOrchestratorAnalysis,
  runForensic,
  hardDeleteTenant,
  viewAsLandlord,
  skorLoginAdmin,
} from "../../context/functions/AdminFunction";
import moment from "moment";
import { getClassName, haveValue } from "../../utils/common";
import { toast } from "react-toastify";
import { constants } from "../../utils/constants";
import axiosInstance from "../../services/authWrapper";
import DeleteModal from "../DeleteModal";
import CsvAnalysisReportModal from "./CsvAnalysisReportModal";
import GroupedActionMenu from "../GroupedActionMenu";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import {
  Check,
  Clock3,
  Download,
  FileText,
  KeyRound,
  LogIn,
  ScanLine,
  Trash2,
  UserRound,
  X,
} from "lucide-react";

countries.registerLocale(enLocale);

const getTenantFullName = (tenant) =>
  `${tenant?.fname || ""} ${tenant?.lname || ""}`.trim() || "Tenant";

const getDocumentDetails = (tenant) => {
  const onfidoDocument = tenant?.onfido_user_kyc?.onfido_doc_data;
  const onfidoType = String(onfidoDocument?.document_type || "").toLowerCase();
  const kycDocumentNumber =
    onfidoType === "passport"
      ? ""
      : onfidoDocument?.personal_number || onfidoDocument?.document_number;
  const dniNie = String(tenant?.dni_nie || kycDocumentNumber || "").trim();
  const passport = String(
    tenant?.passport_id ||
      (onfidoType === "passport" ? onfidoDocument?.document_number : "") ||
      "",
  ).trim();
  const nationality = getCountryName(
    tenant?.nationality || onfidoDocument?.nationality,
  );

  if (dniNie) {
    const explicitType = dniNie.match(/^(DNI|NIE)\s*/i)?.[1]?.toUpperCase();
    const number = dniNie.replace(/^(DNI|NIE)\s*/i, "").trim();
    const type =
      explicitType || (/^[XYZ]/i.test(number) ? "NIE" : "DNI");
    return {
      primary: `${type} ${number}`,
      nationality,
    };
  }

  if (passport) {
    return {
      primary: `Passport ${passport.replace(/^passport\s*/i, "")}`,
      nationality,
    };
  }

  return { primary: "-", nationality: "" };
};

function getCountryName(value) {
  if (!value || typeof value !== "string") return "";
  const trimmed = value.trim();
  if (!trimmed) return "";

  const alpha2 = countries.getAlpha2Code(trimmed, "en") ||
    countries.alpha3ToAlpha2(trimmed.toUpperCase());
  if (alpha2) return countries.getName(alpha2, "en") || trimmed;
  return trimmed;
}

const hasUploadedBankStatement = (tenant) => {
  const source = String(tenant?.kyc_data_from || "").toLowerCase();
  return (
    source.includes("uploaded from csv") ||
    source.includes("statement") ||
    tenant?.bank_statement_uploaded === true ||
    tenant?.bank_statement_uploaded === "true" ||
    tenant?.has_bank_statement === true ||
    tenant?.has_bank_statement === "true"
  );
};

const hasConnectedBankAccount = (tenant) => {
  const explicitBankVerification =
    tenant?.bank_verified === true ||
    tenant?.bank_verified === "true" ||
    tenant?.bank_verified === "yes" ||
    tenant?.bank_verified === "Yes" ||
    tenant?.bank_account_connected === true ||
    tenant?.bank_account_connected === "true" ||
    tenant?.tink_connected === true ||
    tenant?.tink_connected === "true";
  const source = String(tenant?.kyc_data_from || "").toLowerCase();
  return (
    explicitBankVerification ||
    (haveValue(tenant?.paymentScores) && !hasUploadedBankStatement(tenant) &&
      !source.includes("csv"))
  );
};

const getLatestKycStatus = (tenant) => {
  const apiStatus = String(tenant?.kyc_status || "").toLowerCase();
  if (apiStatus === "approved") return "Approved";
  if (apiStatus === "review") return "Review";
  if (apiStatus === "declined") return "Declined";
  if (apiStatus === "not_started") return "Not started";
  // Backend returns "unknown" for a linked KYC record that has not reached a
  // final Onfido status yet — treat it as in progress.
  if (apiStatus === "unknown") return "unknown";

  const workflowStatus = String(
    tenant?.onfido_user_kyc?.onfido_workflow_res?.status ||
      tenant?.onfido_status ||
      tenant?.onfido_status_latest ||
      "",
  ).toLowerCase();

  if (workflowStatus === "declined") return "Declined";
  if (workflowStatus === "approved") return "Approved";
  if (workflowStatus) return "Review";
  if (
    tenant?.onfido_kyc_success === true ||
    tenant?.onfido_kyc_success === "true"
  ) {
    return "Approved";
  }
  if (tenant?.onfido_user_kyc?.onfido_doc) return "Review";
  return "Not started";
};

const isGuarantorValue = (value) =>
  value === true ||
  value === "true" ||
  value === "yes" ||
  value === "Yes";

const getTenantType = (tenant) => {
  if (tenant?.role === "extTenant") return "External tenant";
  else if (tenant?.role === "llTenant") return "Tenant check";
  else if (tenant?.role === "llGuarantor") return "Tenant check (Guarantor)";
  else if(tenant?.role === "emailTenant") return "Email tenant";
  else if (
    (Array.isArray(tenant?.gurantor_for) && tenant.gurantor_for.length > 0)
  ) {
    return "Guarantor";
  }
  return "Tenant";
};

const TenantsList = (props) => {
  const {
    tenants,
    sortColumn,
    sortType,
    handleSorting,
    loadTenants,
    currentPage,
    searchVal,
    sortBy,
    filter,
    searchInputRef,
    handleShowWorkspaceInfo,
  } = props;

  const navigate = useNavigate();

  const { getSingleUser, authState } = useContext(Ucontext);
  const { user: adminUser } = authState;

  const [RentModal, setRentModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detailLoadingId, setDetailLoadingId] = useState(null);
  const [singleTenant, setSingleTenant] = useState();
  const [otpTenant, setOtpTenant] = useState({});
  const [showOtp, setShowOtp] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [tinkModalOpen, setTinkModalOpen] = useState(false);
  const [tinkModalData, setTinkModalData] = useState(false);
  const [csvAnalysisModalOpen, setCsvAnalysisModalOpen] = useState(false);
  const [csvAnalysisResponse, setCsvAnalysisResponse] = useState(null);
  const [deleteModal, setDeleteModal] = useState(null);
  const [isDeletingTenant, setIsDeletingTenant] = useState(false);

  const renderSortIcon = (column) => {
    if (sortColumn !== column) {
      return (
        <FaSort className="sort-icon sort-icon-default" aria-hidden="true" />
      );
    }

    return sortType === "asc" ? (
      <FaSortUp className="sort-icon sort-icon-active" aria-hidden="true" />
    ) : (
      <FaSortDown className="sort-icon sort-icon-active" aria-hidden="true" />
    );
  };

  const showOtpModal = (tenant) => {
    setOtpTenant(tenant);
    setShowOtp(true);
  };

  const handleDownloadCsv = async (tenant_id, isViewModalReport = false) => {
    setLoading(true);

    downloadTinkReport(tenant_id)
      .then(async (response) => {
        // Extract the filename from Content-Disposition
        const blob = new Blob([response.data], { type: "text/csv" });
        const contentDisposition = response.headers["content-disposition"];
        const filename = contentDisposition
          ? contentDisposition.split("filename=")[1]?.replace(/"/g, "")
          : `${tenant_id}-tink-report.csv`;

        if (isViewModalReport) {
          const formData = new FormData();
          formData.append("csv", blob, filename);

          const analysisResponse = await runOrchestratorAnalysis(formData);

          setCsvAnalysisResponse(analysisResponse);
          setCsvAnalysisModalOpen(true);
          setLoading(false);
          return;
        }

        const text = await blob.text();
        const parsed = Papa.parse(text, {
          header: true,
          skipEmptyLines: true, // Ignores blank lines
          dynamicTyping: true, // Converts numeric strings to actual numbers
          transformHeader: (header) => header.trim(), // Remove whitespace around headers
        });
        if (parsed.errors.length) {
          console.error("CSV Parsing Errors", parsed.errors);
          setLoading(false);
          return;
        }

        const rows = parsed.data;

        // Transform rows array into a column-based key-value object
        const columnData = {};

        rows.forEach((row) => {
          for (const key in row) {
            if (!columnData[key]) {
              columnData[key] = [];
            }
            columnData[key].push(row[key]);
          }
        });

        console.log("Transformed Column-Wise Data:", columnData);

        const getColumnValues = (...keys) => {
          const foundKey = keys.find((key) => Array.isArray(columnData[key]));
          return foundKey ? columnData[foundKey] : [];
        };

        const bookedDates = getColumnValues("bookedDate", "Booked Date");
        const amounts = getColumnValues(
          "amountScaledValue",
          "Amount (Scaled Value)",
        );
        const categories = getColumnValues("Category", "category");
        const subCategories = getColumnValues("SubCategory", "subCategory");

        //Getting 90 days indexes
        const today = new Date();
        const ninetyDaysAgo = new Date(today);
        ninetyDaysAgo.setDate(today.getDate() - 90);

        // Step 1: Get recent indexes
        const recentIndexes = bookedDates
          .map((dateStr, index) => {
            const date = new Date(dateStr);
            return date >= ninetyDaysAgo ? index : -1;
          })
          .filter((index) => index !== -1);

        // Step 2–5: Calculate average of absolute values
        let total = 0;
        let count = 0;

        recentIndexes.forEach((i) => {
          const rawAmount = amounts[i];
          if (!rawAmount) return;

          const absVal = Math.abs(parseFloat(rawAmount));
          if (!isNaN(absVal)) {
            total += absVal;
            count++;
          }
        });
        console.log("Total Value", total);
        const average = count > 0 ? total / count : 0;
        const total_transactions = bookedDates.length ?? 0;

        console.log("Absolute Average (last 90 days):", average.toFixed(2));

        let positiveSum = 0;
        let negativeSum = 0;

        amounts.forEach((amountStr) => {
          const value = parseFloat(amountStr);

          if (isNaN(value)) return;

          if (value > 0) {
            positiveSum += value;
          } else if (value < 0) {
            negativeSum += value; // will stay negative
          }
        });
        negativeSum = Math.abs(negativeSum);
        const totalAbsoluteSum = Math.abs(positiveSum) + Math.abs(negativeSum);

        const result = {
          income: {},
          expense: {},
        };

        for (let i = 0; i < amounts.length; i++) {
          const rawAmount = amounts[i];
          const amount = parseFloat(rawAmount);

          if (isNaN(amount)) continue;

          const absAmount = Math.abs(amount);
          const category = categories[i];
          const subCategory = subCategories[i];

          if (!subCategory) continue;

          // Normalize category
          let targetCategory;
          if (category === "Income" || category === "Regular Income") {
            targetCategory = "income";
          } else if (category === "Expense" || category === "Regular Expense") {
            targetCategory = "expense";
          } else {
            continue; // Ignore unknown category types
          }

          // Aggregate by subCategory
          if (!result[targetCategory][subCategory]) {
            result[targetCategory][subCategory] = 0;
          }

          result[targetCategory][subCategory] += absAmount;
        }

        setTinkModalData({
          object: result,
          average: average,
          total_transactions: total_transactions,
          positiveSum: positiveSum,
          negativeSum: negativeSum,
          totalAbsoluteSum: totalAbsoluteSum,
        });

        setTinkModalOpen(true);

        // Save the file
        saveAs(blob, filename);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err, "Error in handleDownloadCsv");
        toast.error("Unable to process Tink report");
        setLoading(false);
      });
  };

  function MyVerticallyCenteredModal(props) {
    if (loading) return <Loading />;
    return (
      <Modal {...props} size="md" centered className="tenants-detail-modal">
        <Modal.Header closeButton className="tenants-modal-header">
          <Modal.Title className="fw-bold h5">User Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="tenants-modal-body">
          <div>
            <Row className="g-3 mb-3">
              <Col md={6}>
                <div className="bg-light p-3 rounded-3 border h-100">
                  <p
                    className="text-muted small mb-1 fw-bold text-uppercase"
                    style={{ fontSize: "11px", letterSpacing: "0.05em" }}
                  >
                    Name
                  </p>
                  <h6 className="mb-0 fw-bold">
                    {singleTenant?.fname} {singleTenant?.lname}
                  </h6>
                </div>
              </Col>
              <Col md={6}>
                <div className="bg-light p-3 rounded-3 border h-100">
                  <p
                    className="text-muted small mb-1 fw-bold text-uppercase"
                    style={{ fontSize: "11px", letterSpacing: "0.05em" }}
                  >
                    DNI/NIE
                  </p>
                  <h6 className="mb-0 fw-bold">
                    {singleTenant?.dni_nie || "-"}
                  </h6>
                </div>
              </Col>
            </Row>
            <Row className="g-3 mb-3">
              <Col md={6}>
                <div className="bg-light p-3 rounded-3 border h-100">
                  <p
                    className="text-muted small mb-1 fw-bold text-uppercase"
                    style={{ fontSize: "11px", letterSpacing: "0.05em" }}
                  >
                    Type Of Employee
                  </p>
                  <h6 className="mb-0 fw-bold">
                    {singleTenant?.type_of_employee || "-"}
                  </h6>
                </div>
              </Col>
              <Col md={6}>
                <div className="bg-light p-3 rounded-3 border h-100">
                  <p
                    className="text-muted small mb-1 fw-bold text-uppercase"
                    style={{ fontSize: "11px", letterSpacing: "0.05em" }}
                  >
                    Nationality
                  </p>
                  <h6 className="mb-0 fw-bold">
                    {singleTenant?.nationality || "-"}
                  </h6>
                </div>
              </Col>
            </Row>
            <Row className="g-3 mb-3">
              <Col md={6}>
                <div className="bg-light p-3 rounded-3 border h-100">
                  <p
                    className="text-muted small mb-1 fw-bold text-uppercase"
                    style={{ fontSize: "11px", letterSpacing: "0.05em" }}
                  >
                    Name Of Company
                  </p>
                  <h6 className="mb-0 fw-bold">
                    {singleTenant?.name_of_company || "-"}
                  </h6>
                </div>
              </Col>
              <Col md={6}>
                <div className="bg-light p-3 rounded-3 border h-100">
                  <p
                    className="text-muted small mb-1 fw-bold text-uppercase"
                    style={{ fontSize: "11px", letterSpacing: "0.05em" }}
                  >
                    Employment Status
                  </p>
                  <h6 className="mb-0 fw-bold">
                    {singleTenant?.employment_status || "-"}
                  </h6>
                </div>
              </Col>
            </Row>
            <Row className="g-3 mb-3">
              <Col md={6}>
                <div className="bg-light p-3 rounded-3 border h-100">
                  <p
                    className="text-muted small mb-1 fw-bold text-uppercase"
                    style={{ fontSize: "11px", letterSpacing: "0.05em" }}
                  >
                    University
                  </p>
                  <h6 className="mb-0 fw-bold">
                    {singleTenant?.name_of_university || "-"}
                  </h6>
                </div>
              </Col>
              <Col md={6}>
                <div className="bg-light p-3 rounded-3 border h-100">
                  <p
                    className="text-muted small mb-1 fw-bold text-uppercase"
                    style={{ fontSize: "11px", letterSpacing: "0.05em" }}
                  >
                    Education
                  </p>
                  <h6 className="mb-0 fw-bold">
                    {singleTenant?.type_of_student || "-"}
                  </h6>
                </div>
              </Col>
            </Row>
            {singleTenant?.previousFeedback?.rating && (
              <Row className="g-3">
                <Col md={12}>
                  <div className="bg-light p-3 rounded-3 border">
                    <p
                      className="text-muted small mb-1 fw-bold text-uppercase"
                      style={{ fontSize: "11px", letterSpacing: "0.05em" }}
                    >
                      Previous Landlord Rating
                    </p>
                    <h6 className="mb-0 fw-bold d-flex align-items-center">
                      {singleTenant.previousFeedback.rating}
                      <AiFillStar color="#F59F18" size={18} className="ms-2" />
                    </h6>
                  </div>
                </Col>
              </Row>
            )}
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  const loadSingleTenant = (userid, type) => {
    console.log("Load single user runs");
    const tenantRes = getSingleUser(userid, type);
    console.log(tenantRes);
  };

  const handleLogin = async (value) => {
    const data = {
      user_id: value._id,
      user_type: "tenant",
    };
    adminLogin(data)
      .then((res) => {
        console.log(res, "CHECK THIS HERE");
        const queryParams = new URLSearchParams({
          token: res.token || "", // Append token
          usertype: "tenant" || "", // Append usertype
          is_verified: res.user.is_verified || true, // Append is_verified
          saveduserid: res.user._id || "", // Append saveduserid
          external_customer_id: res.user._id || "", // Append external_customer_id
          app_type: "skor" || "", // // Append app_type
        });
        console.log(
          new URL(`${REACT_APP_URL}/redirect?${queryParams.toString()}`).href,
          "CHECK HREF HEARE",
        );
        window.open(
          new URL(`${REACT_APP_URL}/redirect?${queryParams.toString()}`).href,
          "_blank",
        );
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleViewAsLandlord = async (tenant) => {
    try {
      const res = await skorLoginAdmin();

      if (res?.success && res?.success_res?.token) {
        const queryParams = new URLSearchParams({
          token: res?.success_res?.token || "",
          usertype: "admin" || "",
          is_verified: res?.success_res?.user.is_verified || true,
          saveduserid: res?.success_res?.user._id || "",
          isAdminUser: "true",
          loggedin_by: "admin",
          redirect_url: `/admin/tenant/${tenant?._id}`
        });
        console.log(
          new URL(`${REACT_APP_URL}/admin-auth?${queryParams.toString()}`).href,
          "ADMIN LOGIN URL",
        );

        window.open(
          new URL(`${REACT_APP_URL}/admin-auth?${queryParams.toString()}`).href,
          "_blank",
        );
      } else {
        toast.error("Error while viewing as landlord");
      }
    } catch (error) {
      console.log(error);
      toast.error("Error logging in as admin");
    }
  }

  const viewDetail = async (value) => {
    setDetailLoadingId(value?._id);
    try {
      const axiosRes = await axiosInstance({
        method: "POST",
        headers: { "x-access-token": localStorage.getItem("token") },
        url: `${BASE_URL}/users/getAllData?type=tenant`,
        data: {
          user_id: value?._id,
        },
      });
      console.log(axiosRes?.data?.tenant, "tenatn data here");
      navigate("/scoreProfile", { state: axiosRes?.data?.tenant });
    } catch (err) {
      console.log(err, "Error in viewDetail");
      toast.error("Unable to load tenant details");
    } finally {
      setDetailLoadingId(null);
    }
  };

  const viewDocument = async (docId) => {
    const formdata = {
      envelopeId: docId,
    };
    downloadDocuSignDoc(formdata)
      .then((res) => {
        console.log(res.zipFile, "Response for download document");

        var binaryString = atob(res.zipFile);
        var len = binaryString.length;
        var bytes = new Uint8Array(len);
        for (var i = 0; i < len; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        // Create blob from Uint8Array
        var blob = new Blob([bytes.buffer], { type: "application/pdf" });

        // Create a temporary link element
        var link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = "document.pdf";

        // Trigger the download
        link.click();
      })
      .catch((err) => {
        console.log(err, "Error while downloading docusign doc");
      });
  };

  const checkIeDetails = (userDetails) => {
    var check = true;

    if (!userDetails?.ie_details_id[0]) {
      check = false;
      console.log("Failing in no entry");
    }
    if (
      userDetails?.ie_student_id != userDetails?.ie_details_id[0]?.student_id
    ) {
      check = false;
      console.log("Failing in studentid");
    }

    return check;
  };

  const calculateTenantScore = (tenant) => {
    const score = tenant?.paymentScores?.creditScore?.value;
    return score !== undefined && score !== null && score !== ""
      ? Number(score)
      : 0;
  };

  const getFinancialSources = (tenant) => ({
    hasBankAccount: hasConnectedBankAccount(tenant),
    hasBankStatement: hasUploadedBankStatement(tenant),
  });

  const handleTenantDelete = async (tenant) => {
    if (isDeletingTenant) return;

    setIsDeletingTenant(true);
    try {
      const response = await hardDeleteTenant(tenant?._id);
      if (response?.success !== true) return;
      if (searchInputRef?.current) {
        searchInputRef.current.value = "";
      }
      loadTenants();
      setDeleteModal(null);
    } finally {
      setIsDeletingTenant(false);
    }
  };

  function nl2br(str) {
    if (typeof str !== "string") return "";
    return str.replace(/\n/g, "<br>");
  }

  const handleDownloadReport = (tenant) => {
    setLoading(true);
    getDocumentAnalysis(tenant?._id)
      .then((res) => {
        console.log(res);
        setShowSummaryModal(true);
        setSummaryData(res?.summary);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  const handleRunForensic = (email) => {
    if (!haveValue(email)) {
      toast.success("Failed to send forensic request");
      return;
    }
    toast.success(`Forensic request sent for ${email}`);
    runForensic(email)
      .then((res) => {})
      .catch((err) => {
        console.log("Error running forensic", err);
      });
  };

  const tenantActionGroups = (tenant) => {
    const hasDocuments = Number(tenant?.tenant_docs_count || 0) > 0;
    const isSuperAdmin = adminUser?.role === constants.SUPER_ADMIN;

    return [
      {
        label: "Access",
        actions: [
          ...(isSuperAdmin
            ? [
                {
                  label: "Login as tenant",
                  icon: <LogIn />,
                  onSelect: () => handleLogin(tenant),
                },
                {
                  label: "View as landlord",
                  icon: <UserRound />,
                  onSelect: () => handleViewAsLandlord(tenant),
                },
              ]
            : []),
          {
            label: "Show OTP",
            icon: <KeyRound />,
            onSelect: () => showOtpModal(tenant),
          },
        ],
      },
      {
        label: "Review",
        actions: [
          {
            label: "Details",
            icon: <FileText />,
            disabled: detailLoadingId === tenant?._id,
            onSelect: () => viewDetail(tenant),
          },
          {
            label: "Show summary",
            icon: <FileText />,
            onSelect: () => handleDownloadReport(tenant),
          },
          {
            label: "Run forensic",
            icon: <ScanLine />,
            disabled: !hasDocuments,
            description: hasDocuments
              ? ""
              : "Needs at least 1 uploaded document",
            onSelect: () => handleRunForensic(tenant?.email),
          },
        ],
      },
    ];
  };

  return (
    <>
      <Table responsive hover className="tenants-table">
        <thead>
          <tr>
            <th
              className="sortable-header"
              onClick={() => handleSorting("fname")}
            >
              <span className="sortable-header-content">
                NAME {renderSortIcon("fname")}
              </span>
            </th>
            <th>JOINED FROM</th>
            <th>ID DOCUMENT</th>
            <th>TENANT TYPE</th>
            <th>KYC</th>
            <th>DOCS UPLOADED</th>
            <th
              className="sortable-header"
              onClick={() => handleSorting("credit_score")}
            >
              <span className="sortable-header-content">
                FINANCIAL SCORE {renderSortIcon("credit_score")}
              </span>
            </th>
            <th>GUARANTOR</th>
            <th>LAST LOGIN</th>
            <th>WORKSPACES</th>
            <th
              className="sortable-header"
              onClick={() => handleSorting("created_at")}
            >
              <span className="sortable-header-content">
                JOINED ON {renderSortIcon("created_at")}
              </span>
            </th>
            <th>ACTIONS</th>
          </tr>
        </thead>
        <tbody>
          {tenants.map((tenant) => {
            const document = getDocumentDetails(tenant);
            const kycStatus = getLatestKycStatus(tenant);
            const isGuarantor = getTenantType(tenant) === "Guarantor";
            const guarantorPresent =
              isGuarantorValue(tenant?.have_gurantor) ||
              Number(tenant?.guarantor_count || 0) > 0;
            const { hasBankAccount, hasBankStatement } =
              getFinancialSources(tenant);
            const hasFinancialSource = hasBankAccount || hasBankStatement;

            return (
              <tr key={tenant?._id}>
                <td>
                  <div className="tenant-name-stack">
                    <span className="tenant-name">
                      {getTenantFullName(tenant)}
                    </span>
                    <span className="tenant-email">
                      {tenant?.email || "N/A"}
                    </span>
                  </div>
                </td>
                <td>
                  {haveValue(tenant?.referredName) &&
                  tenant?.referredName !== "skor" ? (
                    <>
                      <a
                        href={`/landlord?email=${tenant?.referredEmail || tenant?.referredName}`}
                        className="tenant-link"
                      >
                        {tenant?.referredName}
                      </a>
                      {tenant?.agency_name && (
                        <span className="text-muted small">
                          {" "}({tenant?.agency_name})
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-muted small">skor</span>
                  )}
                </td>
                <td>
                  {document.primary === "-" ? (
                    <span className="text-muted">-</span>
                  ) : (
                    <div className="tenant-document-stack">
                      <span className="tenant-document-primary">
                        {document.primary}
                      </span>
                      <span className="tenant-document-secondary">
                        {document.nationality || "Nationality not set"}
                      </span>
                    </div>
                  )}
                </td>
                <td className="fw-500">{getTenantType(tenant)}</td>
                <td>
                  {kycStatus === "Not started" ? (
                    <span className="tenant-kyc-not-started">Not started</span>
                  ) : (
                    <span
                      className={`tenant-kyc-badge tenant-kyc-badge-${kycStatus.toLowerCase()}`}
                    >
                      {kycStatus === "Approved" ? (
                        <Check size={14} aria-hidden="true" />
                      ) : kycStatus === "Review" ? (
                        <Clock3 size={14} aria-hidden="true" />
                      ) : (
                        <X size={14} aria-hidden="true" />
                      )}
                      {kycStatus}
                    </span>
                  )}
                </td>
                <td className="text-center fw-600">
                  {Number(tenant?.tenant_docs_count || 0)}
                </td>
                <td>
                  {hasFinancialSource ? (
                    <div className="tenant-financial-stack">
                      <span className="tenant-financial-score">
                        {calculateTenantScore(tenant)}/1000
                      </span>
                      <span className="tenant-financial-source">
                        {hasBankAccount ? (
                          <>
                            <span>Bank account</span>
                            <span aria-hidden="true"> · </span>
                            <button
                              type="button"
                              className="tenant-report-link"
                              onClick={() =>
                                handleDownloadCsv(tenant?._id, true)
                              }
                            >
                              Tink report
                            </button>
                          </>
                        ) : (
                          "Bank statement"
                        )}
                        {haveValue(tenant?.paymentScores) && (
                          <>
                            {!hasBankAccount && (
                              <button
                                type="button"
                                className="tenant-download-report"
                                title="Open financial report"
                                aria-label="Open financial report"
                                onClick={() =>
                                  handleDownloadCsv(tenant?._id, true)
                                }
                              >
                                <FileText size={14} aria-hidden="true" />
                              </button>
                            )}
                            <button
                              type="button"
                              className="tenant-download-report"
                              title="Download financial report"
                              aria-label="Download financial report"
                              onClick={() => handleDownloadCsv(tenant?._id)}
                            >
                              <Download size={14} aria-hidden="true" />
                            </button>
                          </>
                        )}
                      </span>
                    </div>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td>
                  {isGuarantor ? (
                    <span className="text-muted">-</span>
                  ) : guarantorPresent ? (
                    <span className="tenant-guarantor-yes">Yes</span>
                  ) : (
                    <span className="tenant-guarantor-no">No</span>
                  )}
                </td>
                <td className="text-muted small">
                  {tenant?.lastLoginFrom
                    ? String(tenant.lastLoginFrom).toLowerCase()
                    : "-"}
                </td>
                <td>
                  <button
                    type="button"
                    className="tenant-link tenant-workspace-link"
                    onClick={() => handleShowWorkspaceInfo(tenant?._id)}
                  >
                    View
                  </button>
                </td>
                <td className="text-muted fw-medium small">
                  {moment(tenant?.created_at).format("DD MMM YYYY")}
                </td>
                <td>
                  <GroupedActionMenu
                    groups={tenantActionGroups(tenant)}
                    footerActions={
                      adminUser?.role === constants.SUPER_ADMIN
                        ? [
                            {
                              label: "Delete tenant…",
                              icon: <Trash2 />,
                              danger: true,
                              onSelect: () => setDeleteModal(tenant),
                            },
                          ]
                        : []
                    }
                    className="tenant-actions-dropdown"
                  />
                </td>
              </tr>
            );
          })}
          {tenants?.length === 0 && (
            <tr>
              <td colSpan={12} className="text-center py-5 text-muted">
                No Data Found!
              </td>
            </tr>
          )}
          <MyVerticallyCenteredModal
            show={RentModal}
            onHide={() => setRentModal(false)}
          />
        </tbody>
      </Table>

      <Modal
        show={tinkModalOpen}
        size="lg"
        centered
        onHide={() => {
          setTinkModalData({});
          setTinkModalOpen(false);
        }}
      >
        <Modal.Header closeButton className="tenants-modal-header">
          <Modal.Title className="fw-bold h5">Tink Report Summary</Modal.Title>
        </Modal.Header>
        <Modal.Body className="tenants-modal-body">
          <Row className="g-3 mb-4">
            <Col md={6}>
              <div className="bg-light p-3 rounded-3 border">
                <p
                  className="text-muted small mb-1 fw-bold text-uppercase"
                  style={{ fontSize: "11px", letterSpacing: "0.05em" }}
                >
                  Avg 90 Day Transaction
                </p>
                <h5 className="mb-0 fw-bold">
                  {tinkModalData?.average?.toFixed(2) || "0.00"}
                </h5>
              </div>
            </Col>
            <Col md={6}>
              <div className="bg-light p-3 rounded-3 border">
                <p
                  className="text-muted small mb-1 fw-bold text-uppercase"
                  style={{ fontSize: "11px", letterSpacing: "0.05em" }}
                >
                  Total Transactions
                </p>
                <h5 className="mb-0 fw-bold">
                  {tinkModalData?.total_transactions || 0}
                </h5>
              </div>
            </Col>
            <Col md={6}>
              <div className="bg-light p-3 rounded-3 border">
                <p
                  className="text-muted small mb-1 fw-bold text-uppercase"
                  style={{ fontSize: "11px", letterSpacing: "0.05em" }}
                >
                  Total Turnover
                </p>
                <h5 className="mb-0 fw-bold">
                  {tinkModalData?.totalAbsoluteSum?.toFixed(2) || "0.00"}
                </h5>
              </div>
            </Col>
            <Col md={6}>
              <div className="bg-light p-3 rounded-3 border">
                <p
                  className="text-muted small mb-1 fw-bold text-uppercase"
                  style={{ fontSize: "11px", letterSpacing: "0.05em" }}
                >
                  Income
                </p>
                <h5 className="mb-0 fw-bold text-success">
                  {tinkModalData?.positiveSum?.toFixed(2) || "0.00"}
                </h5>
              </div>
            </Col>
          </Row>
          <div className="row g-4">
            <Col md={6}>
              <div className="bg-light p-3 rounded-3 border">
                <p
                  className="text-muted small mb-2 fw-bold text-uppercase"
                  style={{ fontSize: "11px", letterSpacing: "0.05em" }}
                >
                  Income List
                </p>
                {Object.entries(tinkModalData?.object?.income || {}).length >
                0 ? (
                  <ul className="mb-0 ps-3">
                    {Object.entries(tinkModalData?.object?.income || {}).map(
                      ([subCategory, value]) => (
                        <li key={subCategory} className="mb-1">
                          <span className="fw-500">{subCategory}:</span>{" "}
                          {value.toFixed(2)}
                        </li>
                      ),
                    )}
                  </ul>
                ) : (
                  <p className="text-muted small mb-0">No income data</p>
                )}
              </div>
            </Col>
            <Col md={6}>
              <div className="bg-light p-3 rounded-3 border">
                <p
                  className="text-muted small mb-2 fw-bold text-uppercase"
                  style={{ fontSize: "11px", letterSpacing: "0.05em" }}
                >
                  Expense List
                </p>
                {Object.entries(tinkModalData?.object?.expense || {}).length >
                0 ? (
                  <ul className="mb-0 ps-3">
                    {Object.entries(tinkModalData?.object?.expense || {}).map(
                      ([subCategory, value]) => (
                        <li key={subCategory} className="mb-1">
                          <span className="fw-500">{subCategory}:</span>{" "}
                          {value.toFixed(2)}
                        </li>
                      ),
                    )}
                  </ul>
                ) : (
                  <p className="text-muted small mb-0">No expense data</p>
                )}
              </div>
            </Col>
          </div>
        </Modal.Body>
      </Modal>
      <CsvAnalysisReportModal
        show={csvAnalysisModalOpen}
        analysisResponse={csvAnalysisResponse}
        onHide={() => {
          setCsvAnalysisResponse(null);
          setCsvAnalysisModalOpen(false);
        }}
      />
      <Modal
        show={showOtp}
        size="md"
        centered
        onHide={() => {
          setShowOtp(false);
          setOtpTenant({});
        }}
      >
        <Modal.Header closeButton className="tenants-modal-header">
          <Modal.Title className="fw-bold h5">
            OTP for {otpTenant?.fname || ""} {otpTenant?.lname || ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="tenants-modal-body">
          <div className="bg-light p-4 rounded-3 border text-center">
            <p
              className="text-muted small mb-2 fw-bold text-uppercase"
              style={{ fontSize: "11px", letterSpacing: "0.05em" }}
            >
              One-Time Password
            </p>
            <h3
              className="fw-bold mb-0"
              style={{ fontSize: "28px", letterSpacing: "8px" }}
            >
              {otpTenant?.otp || "-----"}
            </h3>
          </div>
        </Modal.Body>
      </Modal>
      <Modal
        show={showSummaryModal}
        onHide={() => {
          setShowSummaryModal(false);
          setSummaryData("");
        }}
        centered
        size="lg"
      >
        <Modal.Header closeButton className="tenants-modal-header">
          <Modal.Title className="fw-bold h5">Tenant Summary</Modal.Title>
        </Modal.Header>
        <Modal.Body className="tenants-modal-body">
          <div className="bg-light p-4 rounded-3 border">
            <p
              dangerouslySetInnerHTML={{
                __html: nl2br(summaryData),
              }}
              className="mb-0"
            />
          </div>
        </Modal.Body>
      </Modal>
      <DeleteModal
        isOpen={Boolean(deleteModal)}
        onClose={() => setDeleteModal(null)}
        onConfirm={() => handleTenantDelete(deleteModal)}
        isLoading={isDeletingTenant}
        title={`Delete ${getTenantFullName(deleteModal)}?`}
        message="This cannot be undone."
      />
    </>
  );
};

export default TenantsList;
